<template>
  <div>
    <button type="button" class="audit-launcher" aria-haspopup="dialog" :aria-expanded="String(dialog)" @click="open">
      <span class="launcher-icon" aria-hidden="true"><v-icon>mdi-phone-outline</v-icon></span>
      <span class="launcher-copy">
        <span class="launcher-title">Audit Callings <span class="audit-count" :aria-label="auditCount === null ? 'Anzahl noch nicht verfügbar' : auditCount + ' Audit Callings'">{{ auditCount === null ? '–' : auditCount }}</span></span>
        <span class="launcher-description">Gesprächsprotokolle und Feedback ansehen.</span>
      </span>
      <v-icon class="launcher-arrow" aria-hidden="true">mdi-chevron-right</v-icon>
    </button>
    <v-dialog v-model="dialog" dark scrollable max-width="74rem" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="audit-dialog">
        <v-card-title class="dialog-heading">
          <h2 id="studio-audits-title" ref="dialogTitle" tabindex="-1">{{ inStudio ? selectedName + ' · Audit Callings' : 'Audit Callings' }}</h2>
          <v-btn icon dark aria-label="Audit Callings schliessen" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text ref="dialogBody" class="dialog-body">
          <section class="studio-audits" aria-labelledby="studio-audits-title" :aria-busy="String(loading)">
            <header class="audit-heading">
              <div>
                <v-btn v-if="inStudio" text dark color="#94deb0" class="back-to-studios" @click="backToStudios"><v-icon left>mdi-arrow-left</v-icon>Zurück zu den Studios</v-btn>
                <p v-else>{{ allStudios ? 'Alle Studios deiner Organisation.' : 'Die Audit Callings deiner zugeordneten Studios.' }} Wähle ein Studio, um die Gesprächsprotokolle anzusehen.</p>
              </div>
              <v-btn outlined dark color="#94deb0" :loading="loading" @click="load">Aktualisieren</v-btn>
            </header>
            <v-alert v-if="error" type="error" text role="alert">{{ error }}</v-alert>
            <v-progress-linear v-if="loading" indeterminate color="#58d388" class="mb-4" />
            <template v-if="auditCenters.length || (inStudio && studioId)">
              <template v-if="!inStudio">
                <v-text-field v-if="auditCenters.length > 6" v-model="search" label="Studio suchen" prepend-inner-icon="mdi-magnify" dark outlined dense clearable hide-details class="studio-search" />
                <div class="studio-grid" aria-label="Studios mit Audit Callings">
                  <button v-for="center in visibleCenters" :key="center._id" type="button" class="studio-card" :aria-label="center.name + ': Audit Callings öffnen'" @click="selectCenter(center._id)">
                    <span class="studio-folder-heading"><v-icon aria-hidden="true">mdi-folder-outline</v-icon><span class="studio-name">{{ center.name }}</span><v-icon class="folder-arrow" aria-hidden="true">mdi-chevron-right</v-icon></span>
                    <span class="studio-count">{{ center.count }} {{ center.count === 1 ? 'Audit' : 'Audits' }}</span>
                    <span class="studio-date">{{ center.date ? 'Letztes Audit: ' + date(center.date) : 'Noch kein Audit erfasst' }}</span>
                  </button>
                </div>
                <p v-if="!visibleCenters.length" class="audit-empty">Kein Studio für diese Suche gefunden.</p>
              </template>
              <section v-if="inStudio && !loading && !error && studioId" class="studio-records" aria-live="polite" aria-labelledby="studio-records-title">
                <header class="records-heading"><h3 id="studio-records-title">{{ selectedName }}</h3><span>{{ total }} Gesprächseinträge</span></header>
                <p v-if="!rows.length" class="audit-empty">Für dieses Studio wurden noch keine Audit Callings erfasst.</p>
                <article v-for="row in rows" :key="row._id" class="audit-record">
                  <header class="record-heading">
                    <div><h4>{{ row.employeeName }}</h4><p>{{ date(row.date) }} · {{ row.time }} Uhr</p></div>
                    <span class="audit-grade">Note {{ row.grade }}</span>
                  </header>
                  <p>Gesprächsführung: <strong>{{ conversationLabel(row.conversation) }}</strong></p>
                  <div v-if="row.positive || row.potential" class="assessment-grid">
                    <div v-if="row.positive"><h5>Positiv aufgefallen</h5><p class="audit-text">{{ row.positive }}</p></div>
                    <div v-if="row.potential"><h5>Potentialfelder</h5><p class="audit-text">{{ row.potential }}</p></div>
                  </div>
                  <div class="feedback-grid">
                    <div><h5>Feedback Mitarbeiter</h5><p>{{ feedbackLabel(row.employeeFeedback) }}</p></div>
                    <div><h5>Feedback Centerleitung</h5><p>{{ feedbackLabel(row.centerFeedback) }}</p></div>
                  </div>
                </article>
                <v-pagination v-if="total > 25" v-model="page" :length="Math.ceil(total / 25)" :total-visible="5" dark color="#009640" @input="load" />
              </section>
            </template>
            <p v-else-if="!loading && !error" class="audit-empty">{{ !allStudios && !centers.length ? 'Deinem Mitarbeiterprofil ist noch kein Studio zugeordnet.' : 'Noch keine Audit Callings vorhanden.' }}</p>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
export default {
  name: "StudioAuditCallings",
  data: () => ({ dialog: false, inStudio: false, loaded: false, centers: [], rows: [], studioId: null, allStudios: false, total: 0, page: 1, search: '', loading: false, error: '', loadId: 0 }),
  computed: {
    auditCount() { return this.loaded && !this.error ? this.centers.reduce((count, center) => count + (Number(center.count) || 0), 0) : null },
    auditCenters() { return this.centers.filter(center => Number(center.count) > 0) },
    visibleCenters() {
      const query = (this.search || '').trim().toLocaleLowerCase('de-CH')
      return this.auditCenters.filter(center => (center.name || '').toLocaleLowerCase('de-CH').includes(query))
    },
    selectedName() { return (this.centers.find(center => center._id === this.studioId) || {}).name || 'Studio' },
  },
  mounted() { this.load() },
  beforeDestroy() { this.loadId++ },
  methods: {
    open() { this.dialog = true; this.inStudio = false; this.page = 1; this.studioId = null; this.search = ''; this.load() },
    backToStudios() { this.inStudio = false; this.resetScroll() },
    resetScroll() {
      this.$nextTick(() => {
        const body = this.$refs.dialogBody
        const element = body && (body.$el || body)
        if (element) element.scrollTop = 0
        if (this.$refs.dialogTitle) this.$refs.dialogTitle.focus()
      })
    },
    date(value) { return value ? value.split('-').reverse().join('.') : '–' },
    conversationLabel(value) { return { yes: 'Ja', no: 'Nein', partly: 'Teilweise' }[value] || '–' },
    feedbackLabel(value) { return value && value.date ? `${value.by} am ${this.date(value.date)}` : 'Noch offen' },
    selectCenter(id) { this.studioId = id; this.page = 1; this.inStudio = true; this.resetScroll(); this.load() },
    async load() {
      const request = ++this.loadId
      this.loading = true; this.error = ''; this.rows = []
      try {
        const data = await api.academyControl.studioAudits(this.studioId, this.page)
        if (request !== this.loadId) return
        this.centers = data.centers; this.rows = data.rows; this.total = data.total
        this.studioId = data.studioId; this.allStudios = data.allStudios; this.loaded = true
        if (!this.rows.length && this.page > 1) { this.page = Math.max(1, Math.ceil(this.total / 25)); return this.load() }
      } catch (error) {
        if (request !== this.loadId) return
        this.centers = []; this.total = 0; this.studioId = null; this.page = 1; this.loaded = false; this.inStudio = false
        this.error = (error.response && error.response.data && error.response.data.error) || 'Audit Callings konnten nicht geladen werden. Bitte erneut versuchen.'
      } finally { if (request === this.loadId) this.loading = false }
    },
  },
}
</script>

<style scoped>
.audit-launcher { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: start; gap: 0.75rem; padding: 1rem 0.5rem; width: 100%; border-radius: 0.5rem; text-align: left; color: #f4f4f5; }
.audit-launcher:hover, .audit-launcher:focus { background: rgba(255, 255, 255, 0.045); }
.audit-launcher:focus-visible { outline: 2px solid #7ee5a6; outline-offset: 2px; }
.launcher-icon { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: #7ee5a6; background: rgba(0, 150, 64, 0.13); }
.launcher-icon .v-icon { color: currentColor; font-size: 1.25rem; }
.launcher-copy { min-width: 0; overflow-wrap: anywhere; }
.launcher-title { display: flex; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.25rem; font-size: 1rem; font-weight: 700; line-height: 1.5; }
.audit-count { display: inline-block; padding: 0.125rem 0.5rem; border-radius: 1rem; color: #b8f0cb; background: #294433; font-size: 0.75rem; font-weight: 600; }
.launcher-description { display: block; color: #b8b8bc; font-size: 0.875rem; line-height: 1.5; }
.launcher-arrow { margin-top: 0.25rem; color: #b8b8bc; font-size: 1.25rem; }
.audit-dialog { background: #191e1b !important; color: #f4f4f5; border-radius: 1rem; }
.audit-dialog .dialog-heading { display: flex; justify-content: space-between; align-items: center; flex-wrap: nowrap; gap: 1rem; padding: 1rem 1.5rem; border-bottom: 1px solid #39463e; }
.dialog-heading h2 { min-width: 0; font-size: 1.375rem; line-height: 1.4; margin: 0; overflow-wrap: anywhere; }
.audit-dialog .dialog-body { padding: 1.5rem; overscroll-behavior: contain; }
@media (max-width: 40rem) { .audit-dialog .dialog-heading, .audit-dialog .dialog-body { padding: 1rem; } }

.studio-audits { color: #f4f4f5; font-size: 1rem; }
.audit-heading, .records-heading, .record-heading { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
.audit-heading { margin-bottom: 1.25rem; }
.audit-heading > div { flex: 1; min-width: min(100%, 15rem); }
.audit-heading p, .record-heading p { color: #b8c0bb; font-size: 0.875rem; line-height: 1.5; margin: 0; }
.audit-heading .v-btn { font-size: 0.875rem; text-transform: none; }
.studio-search { margin-bottom: 1rem; max-width: 30rem; font-size: 1rem; }
.studio-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr)); gap: 0.75rem; }
.studio-card { display: flex; flex-direction: column; align-items: flex-start; gap: 0.375rem; padding: 1rem; min-width: 0; text-align: left; color: inherit; background: #202622; border: 1px solid #39463e; border-radius: 0.875rem; overflow-wrap: anywhere; }
.studio-card:hover { border-color: #94deb0; background: #263c2e; }
.studio-folder-heading { display: flex; align-items: center; gap: 0.625rem; width: 100%; }
.studio-folder-heading .v-icon { color: #94deb0; font-size: 1.25rem; }
.studio-folder-heading .studio-name { min-width: 0; flex: 1; }
.studio-folder-heading .folder-arrow { color: #b8c0bb; }
.back-to-studios { max-width: 100%; height: auto !important; min-height: 2.75rem; white-space: normal; }
.dialog-heading h2:focus { outline: none; }
.studio-card:focus-visible { outline: 2px solid #94deb0; outline-offset: 3px; }
.studio-name { font-size: 1rem; font-weight: 600; }
.studio-count { font-size: 0.875rem; color: #94deb0; }
.studio-date { font-size: 0.875rem; color: #b8c0bb; }
.studio-records { margin-top: 0; }
.records-heading { align-items: center; margin-bottom: 1rem; }
.records-heading h3 { font-size: 1.25rem; margin: 0; }
.records-heading > span { font-size: 0.875rem; color: #b8c0bb; }
.audit-record { padding: 1.125rem; border: 1px solid #39463e; border-radius: 0.875rem; background: #1c211e; margin-bottom: 0.875rem; overflow-wrap: anywhere; }
.record-heading { margin-bottom: 1rem; }
.record-heading h4 { font-size: 1.125rem; line-height: 1.5; margin: 0 0 0.25rem; }
.audit-grade { padding: 0.375rem 0.75rem; font-size: 0.875rem; color: #b8f0cb; background: #294433; border-radius: 0.5rem; }
.audit-record h5 { font-size: 1rem; line-height: 1.5; margin: 0 0 0.375rem; }
.audit-record > p, .assessment-grid p, .feedback-grid p { font-size: 1rem; line-height: 1.6; margin: 0; }
.assessment-grid, .feedback-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.25rem; margin-top: 1.125rem; }
.feedback-grid { padding-top: 1rem; border-top: 1px solid #39463e; }
.feedback-grid p { color: #b8c0bb; font-size: 0.875rem; }
.audit-text { white-space: pre-wrap; }
.audit-empty { padding: 1.5rem 1rem; color: #b8c0bb; font-size: 1rem; text-align: center; }
@media (max-width: 40rem) { .assessment-grid, .feedback-grid { grid-template-columns: minmax(0, 1fr); } }
</style>
